import { EncodingValidationRequest, ItemIdentifiers, ProductionOrder, ProductionOrderRowDetail } from 'core/api'
import {
  EncodingExtensions as Base,
  GetProductionOrderDetails,
  OverrideProductInfoFieldsContext
} from 'core/extensions/EncodingExtensions'
import { EncodingOperationConfig } from 'core/shared/RemoteOperation'
import { __, T } from 'stylewhere/shared/i18n'

export class EncodingExtensions extends Base {
  static getProductionOrderFields = (_operation?: EncodingOperationConfig) => [
    { label: __(T.custom.reference), path: 'attributes.codePoFather' }
  ]

  static getProductionOrderDetails = ({
    productionOrderRow,
    encodingValidationResponse
  }: GetProductionOrderDetails) => {
    const productionOrder = productionOrderRow?.productionOrder || ({} as ProductionOrder)
    const productionOrderCode = productionOrder?.code || ''
    if (productionOrderCode.startsWith('9999')) {
      return {
        data: { ...productionOrder, attributes: { ...productionOrder.attributes, codePoFather: productionOrderCode } },
        subtitle: productionOrder?.attributes?.codePoFather || '---'
      }
    }
    const carlav = encodingValidationResponse?.item?.attributes?.carlav || productionOrder?.code || ''
    return { subtitle: carlav, data: productionOrder }
  }

  static getOverrideProductInfoFields = (
    context: OverrideProductInfoFieldsContext,
    _operation: EncodingOperationConfig
  ) => {
    switch (context) {
      case 'encodingForm':
        return [
          { path: 'style.value', label: __(T.misc.style) },
          { path: 'material.value', label: __(T.misc.material) },
          { path: 'color.value', label: __(T.misc.color) },
          { path: 'size.value', label: __(T.misc.size) },
          { path: 'attributes.varianttype.value', label: __(T.custom.variantType) },
          { path: 'attributes.variantmodel.value', label: __(T.custom.variantModel) },
          { path: 'attributes.cardcode.value', label: __(T.custom.cardCode) },
          { path: 'attributes.labelcode.value', label: __(T.custom.label) },
          { path: 'attributes.halfpair.value', label: __(T.custom.halfpair) }
        ]
      default:
        return null
    }
  }

  static check_tag_is_logic_killable = (data) => {
    return data.attributes && data.attributes.logicKillable === 'true'
  }

  static async verify(encodingValidationInput: EncodingValidationRequest, addCodesFromValidationResponse = false) {
    const { encodingValidation, encodingValidationResponse } = await super.verify(
      encodingValidationInput,
      addCodesFromValidationResponse
    )
    if (encodingValidationResponse && encodingValidationResponse?.errors) {
      encodingValidationResponse?.errors.forEach((error) => {
        const index = encodingValidation.identifiers.findIndex((idf) => idf.code === error.ref)

        if (index >= 0 && error.errorCode === 'Not Associated Tag') {
          // voglio mostrare i tag vergini di colore grigio/arancio
          encodingValidation.identifiers[index]._colorStatus = 'IGNORED'
          encodingValidation.identifiers[index]._customStatusText = __(T.misc.virgin_tag)
        }
      })
    }

    encodingValidation?.identifiers?.forEach((identifier) => {
      if (identifier._status === 'CONFIRMED') {
        identifier._customStatusText = __(T.custom.TAG_STATUS_CONFIRMED)
      }
    })
    return { encodingValidation, encodingValidationResponse }
  }

  static async onReplaceTagPressedExtension(encodingValidation: EncodingValidationRequest) {
    let proceed = true
    await Promise.all(
      encodingValidation.identifiers
        .filter((idt) => idt.code && !!idt.code && ['UHF_TAG', 'NFC_Tag', 'NFC_TAG'].includes(idt.identifierType))
        .map(async (identifier) => {
          if (!proceed) return
          try {
            const res = (await ItemIdentifiers.search({ equalCodes: identifier.code, size: 1 })).content
            if (res && res.length === 1 && proceed) {
              const tipoTag =
                res[0].attributes && res[0].attributes.tipoTag ? parseInt(res[0].attributes.tipoTag + '', 10) : 0
              proceed = res[0].attributes && [1, 2].includes(tipoTag)
            } else {
              proceed = false
            }
          } catch (e) {
            proceed = false
          }
        })
    )
    if (proceed) return proceed
    throw Error(__(T.error.replace_tag_not_allowed_for_this_tag))
  }

  static getOrderedProductionOrderRows = (productionOrderRows: ProductionOrderRowDetail[]) => {
    return productionOrderRows.sort((a, b) => {
      if (a.product?.size?.value && b.product?.size?.value) {
        return a.product?.size?.value.localeCompare(b.product?.size?.value)
      }
      return 0
    })
  }

  static activeReplacedAssocitionForSimpleItemIdentifier() {
    return true
  }

  static forceSimpleItemIdentifierAsOptional() {
    return true
  }
}
